import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@vinnova/translation';
import { ToastService } from '@vinnova/ui';
import { Observable } from 'rxjs';
import { SearchResultsCommand } from '../../../../core/commands/search-results.command';
import { Country } from '../../../../core/models/country.model';
import { ListCompany } from '../../../../core/models/list-company.model';
import { ListWorksite } from '../../../../core/models/list-worksite.model';
import { SearchResult } from '../../../../core/models/search-result.model';
import { WorksitesQuery } from '../../../../core/queries/worksites.query';
import { SearchResultsRepository } from '../../../../core/repositories/search-result.repository';

@UntilDestroy()
@Component({
  selector: 'lib-search-result-item',
  templateUrl: './search-result-item.component.html',
  styleUrls: ['./search-result-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SearchResultItemComponent implements OnInit, OnDestroy {
  private searchResultsRepository = inject(SearchResultsRepository);
  private toastService = inject(ToastService);
  private worksitesQuery = inject(WorksitesQuery);
  private searchResultsCommand = inject(SearchResultsCommand);
  private transalteService = inject(TranslateService);
  @Input() company: ListCompany;
  @Input() minified: boolean;
  @Input() countries: Country[];
  @Input() alreadySelectedWorksites: string[];
  @Input() alreadySelectedFinanciers: string[];
  @Input() isFinancierSearch: false;
  @Output() selectWorksite = new EventEmitter<unknown>();
  @Output() createWorksite = new EventEmitter<ListCompany>();
  @Output() extendWorksites = new EventEmitter<unknown>();
  isCollapsed = false;
  isSelecting = signal(undefined);
  showAllWorksitesParameter$: Observable<boolean>;
  isCollapsedParameter$: Observable<boolean>;
  isLoading: boolean;
  extendedWorksites$: Observable<string[]>;
  unavailableWorksites$: Observable<string[]>;
  @ViewChild('companyTitle') companyTitle;
  getActiveLang = signal(this.transalteService.activeLang);

  ngOnInit() {
    this.extendedWorksites$ = this.searchResultsRepository.extendedWorksites$;
    this.unavailableWorksites$ =
      this.searchResultsRepository.unavailableWorksites$;
    this.showAllWorksitesParameter$ = this.searchResultsRepository.selectUi(
      this.company.providerId,
      'showAllWorksitesParameter',
    );
    this.isCollapsedParameter$ = this.searchResultsRepository.selectUi(
      this.company.providerId,
      'isCollapsed',
    );
    this.isCollapsedParameter$
      .pipe(untilDestroyed(this))
      .subscribe((val) => (this.isCollapsed = val));

    if (!this.alreadySelectedWorksites) {
      this.alreadySelectedWorksites = [];
    }

    if (!this.alreadySelectedFinanciers) {
      this.alreadySelectedFinanciers = [];
    }
  }
  ngOnDestroy(): void {
    this.searchResultsRepository.resetUnavailableWorksites();
  }

  isWorksiteExtended(worksiteId: string, extendedWorksites: string[]) {
    return extendedWorksites.includes(worksiteId);
  }

  onCollapse() {
    this.searchResultsCommand.updateIsCollapsedParameter(
      this.company.providerId,
      !this.isCollapsed,
    );
  }

  onCreateWorksite(event) {
    event.stopPropagation();
    this.createWorksite.emit(this.company);
  }

  onSelectWorksite(event, worksite: ListWorksite) {
    //preventDefault för att inte submitta formuläret som ligger i bakgrunden
    event.preventDefault();
    if (this.isSelecting()) {
      return;
    }
    this.isSelecting.set(worksite.providerId);
    this.selectWorksite.emit(worksite);
  }

  onShowAllWorksites(event) {
    this.searchResultsCommand.updateShowAllWorksitesParameter(
      this.company.providerId,
      false,
    );
    this.isLoading = true;
    this.searchWorksitesByCompanyId()
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (searchResult) => {
          for (const company of searchResult.result) {
            if (
              company.name === this.company.name &&
              company.providerId === this.company.providerId
            ) {
              const worksitesToAdd = [];
              for (const worksite of company.worksites) {
                {
                  const existingWorksite = this.company.worksites.find(
                    (w) => w.providerId === worksite.providerId,
                  );
                  if (existingWorksite) {
                    worksitesToAdd.push(existingWorksite);
                    continue;
                  }
                  const country = this.countries.find(
                    (country) => country.value === worksite.country,
                  );
                  worksite.country = country ? country.label : '';
                  worksitesToAdd.push(worksite);
                }
              }
              this.searchResultsCommand.updateCompany(
                this.company.providerId,
                worksitesToAdd,
              );
            }
          }
          this.isLoading = false;
        },
        error: () => {
          this.toastService.error('Något gick fel, prova igen');
          this.isLoading = false;
        },
      });
    this.isCollapsed = false;
    this.onscrollToCompanyTitle();
    event.stopPropagation();
  }

  onscrollToCompanyTitle() {
    this.companyTitle?.nativeElement.scrollIntoView({
      behavior: 'smooth',
    });
  }

  isWorksiteConflict(worksiteProviderId: string): boolean {
    return (
      this.isFinancierSearch &&
      this.alreadySelectedWorksites.includes(worksiteProviderId) &&
      !this.alreadySelectedFinanciers.includes(worksiteProviderId)
    );
  }

  isWorksiteAlreadySelected(worksiteProviderId: string): boolean {
    return (
      this.isFinancierSearch &&
      this.alreadySelectedWorksites.includes(worksiteProviderId) &&
      this.alreadySelectedFinanciers.includes(worksiteProviderId)
    );
  }

  private searchWorksitesByCompanyId(): Observable<SearchResult<ListCompany>> {
    return this.worksitesQuery.search(
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      this.company.providerId,
    );
  }

  onShowExtendedWorksites(worksite: ListWorksite, companyId: string, el) {
    const loader = el.parentElement.nextElementSibling;
    loader.style.display = 'block';
    el.parentElement.remove();

    this.extendWorksites.emit({
      worksite,
      companyId,
      loader,
    });
  }
}
